/**
 * Pure logic for the CRM account page (A7Y-4982) and the this-week view
 * (A7Y-4984): history order, filter and paging; the open/closed roster split;
 * contact names; the week grouping. No React, so it is tested directly.
 *
 * Dates are `YYYY-MM-DD` strings throughout and are compared as strings. "Today"
 * is the viewer's local calendar day, and weeks start on Monday, as the CRM's
 * `weekPlans/{weekStart}` ids do.
 */
import type { CrmContactDoc, CrmEventDoc, CrmRosterItemDoc } from '@a7y/types';
import { helmTriggerFallback } from '@a7y/platform-health/helm';

/** History lines shown per page; the largest account section is ~12 KB of history. */
export const HISTORY_PAGE_SIZE = 20;

// MARK: Dates

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Noon local time, so adding days never trips over a DST change. */
function parseDate(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
}

export function addDays(date: string, days: number): string {
  const d = parseDate(date);
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

/** The Monday on or before `date`. */
export function mondayOf(date: string): string {
  const offset = (parseDate(date).getDay() + 6) % 7;
  return addDays(date, -offset);
}

export function weekdayName(date: string): string {
  return parseDate(date).toLocaleDateString('en-GB', { weekday: 'long' });
}

// MARK: History

/** Newest first; a stable order within a day (later-created first). */
export function historyNewestFirst(events: readonly CrmEventDoc[]): CrmEventDoc[] {
  return [...events].sort((a, b) =>
    a.date !== b.date ? (a.date < b.date ? 1 : -1) : a.createdAt < b.createdAt ? 1 : -1
  );
}

/** The distinct source kinds an account's history carries, alphabetically. */
export function sourceKinds(events: readonly CrmEventDoc[]): string[] {
  return [...new Set(events.map((e) => e.source))].sort((a, b) => a.localeCompare(b));
}

export function filterBySource(events: readonly CrmEventDoc[], source: string | null): CrmEventDoc[] {
  return source === null ? [...events] : events.filter((e) => e.source === source);
}

// MARK: Roster and contacts

export interface RosterSplit {
  open: CrmRosterItemDoc[];
  closed: CrmRosterItemDoc[];
}

/**
 * The day an open item is due on (A7Y-5873): its `due`, except a trigger whose
 * `where` names an earlier fallback — "if nothing happens first" has then
 * already passed, so the item is due on that fallback, not on its later date.
 */
export function rosterDue(item: CrmRosterItemDoc): string {
  const fallback = item.kind === 'trigger' ? helmTriggerFallback(item.where) : null;
  return fallback !== null && fallback < item.due ? fallback : item.due;
}

/** Open items by due date (soonest first); closed ones most recently closed first. */
export function splitRoster(items: readonly CrmRosterItemDoc[]): RosterSplit {
  const open = items
    .filter((i) => i.status === 'open')
    .sort((a, b) => {
      const [da, db] = [rosterDue(a), rosterDue(b)];
      return da === db ? a.title.localeCompare(b.title) : da < db ? -1 : 1;
    });
  const closed = items
    .filter((i) => i.status === 'closed')
    .sort((a, b) => ((a.closedOn ?? a.due) < (b.closedOn ?? b.due) ? 1 : -1));
  return { open, closed };
}

/**
 * True when the next date is only a trigger's fallback (A7Y-5342): every open item due that
 * day is a `trigger`, so the date is "if nothing happens first", not a planned touch.
 */
export function nextDateIsTriggerFallback(items: readonly CrmRosterItemDoc[], nextDate: string | null): boolean {
  if (!nextDate) return false;
  const due = items.filter((i) => i.status === 'open' && rosterDue(i) === nextDate);
  return due.length > 0 && due.every((i) => i.kind === 'trigger');
}

/** Primary first, then current contacts, then departed ones, each by name. */
export function orderContacts(contacts: readonly CrmContactDoc[]): CrmContactDoc[] {
  const rank = (c: CrmContactDoc) => (c.primary ? 0 : c.departed ? 2 : 1);
  return [...contacts].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

export function contactName(id: string | null, contacts: readonly CrmContactDoc[]): string | null {
  if (id === null) return null;
  return contacts.find((c) => c.id === id)?.name ?? id;
}

/**
 * The open roster items due on `day` (A7Y-5492): This week's day groups and the HELM
 * Today tab's "Due today" both read this, from the same open-items query, so the two
 * pages cannot count a day differently.
 */
export function rosterDueOn(items: readonly CrmRosterItemDoc[], day: string): CrmRosterItemDoc[] {
  return splitRoster(items).open.filter((i) => rosterDue(i) === day);
}

/** The open roster items due before `today`, oldest first (A7Y-5873): This week's Overdue and HELM Today's. */
export function rosterOverdue(items: readonly CrmRosterItemDoc[], today: string): CrmRosterItemDoc[] {
  return splitRoster(items).open.filter((i) => rosterDue(i) < today);
}

/** An account's section notes without the imported `Next date (file):` line (A7Y-5873): the page shows one next date. */
export function notesForDisplay(notes: string): string {
  return notes
    .split(/\r?\n/)
    .filter((line) => !/^\s*(?:[-*]\s+)?Next date \(file\):/.test(line))
    .join('\n');
}

// MARK: This week

export interface WeekGroup {
  /** `overdue`, a `YYYY-MM-DD` day of the current week, or `next-week`. */
  id: string;
  label: string;
  items: CrmRosterItemDoc[];
}

export interface WeekGroups {
  weekStart: string;
  groups: WeekGroup[];
  /** Open items due after next week: counted, not listed. */
  later: number;
}

/**
 * Open roster items grouped for the this-week view: overdue (due before today),
 * each remaining day of the current week from today to Sunday, and next week
 * (its Monday to Sunday). A day earlier in the week than today has no group of
 * its own: anything due then is overdue.
 */
export function groupThisWeek(items: readonly CrmRosterItemDoc[], today: string): WeekGroups {
  const weekStart = mondayOf(today);
  const nextWeekStart = addDays(weekStart, 7);
  const nextWeekEnd = addDays(weekStart, 13);
  const open = splitRoster(items).open;

  const days: string[] = [];
  for (let d = today; d < nextWeekStart; d = addDays(d, 1)) days.push(d);

  const groups: WeekGroup[] = [
    { id: 'overdue', label: 'Overdue', items: rosterOverdue(items, today) },
    ...days.map((day) => ({
      id: day,
      label: `${day === today ? 'Today' : weekdayName(day)} · ${day}`,
      items: rosterDueOn(items, day),
    })),
    {
      id: 'next-week',
      label: `Next week · ${nextWeekStart} to ${nextWeekEnd}`,
      items: open.filter((i) => rosterDue(i) >= nextWeekStart && rosterDue(i) <= nextWeekEnd),
    },
  ];
  return { weekStart, groups, later: open.filter((i) => rosterDue(i) > nextWeekEnd).length };
}
